import { noDistanceNoteTypes } from '~/consts';
import { FrequencyRecord, Note, PlayerNote } from '~/interfaces';
import { calcDistance } from '~/modules/game-engine/game-state/helpers/calc-distance';
import detectVibrato from '~/modules/game-engine/game-state/helpers/detect-vibrato';

const SINGING_BREAK_TOLERANCE_MS = 100;

/**
 * How near a sung note has to be to the written note's length to count as held perfectly.
 *
 * A flat half beat was nearly automatic on a short note - anything at all lands within half a beat
 * of half a beat - and about two and a half per cent on a twenty beat one, which is stricter than
 * anybody sings. So it is the larger of the two: the old floor, so short notes are no harder than
 * before, and a share of the note's own length, so long ones are reachable.
 *
 * Keyed by the engine tolerance: 1 Hard, 2 Medium, 3 Easy. Hard keeps the original rule; the easier
 * levels forgive a looser hold, and the debug widths above Easy get Easy's.
 */
const PERFECT_HOLD: Record<number, { floor: number; share: number }> = {
  1: { floor: 0.5, share: 0.15 },
  2: { floor: 0.6, share: 0.22 },
  3: { floor: 0.75, share: 0.3 },
};

export const perfectLengthTolerance = (noteLength: number, tolerance: number) => {
  const { floor, share } = PERFECT_HOLD[Math.min(Math.max(tolerance, 1), 3)];

  return Math.max(floor, share * noteLength);
};

// How well the note was held, from whatever it currently holds. Called wherever `length` or the
// frequency records move - the length is rounded up to the written note's end after the singer moves
// on, and judging only before that rounding denied a note the perfect it had just earned.
const judgeHold = (playerNote: PlayerNote, tolerance: number) => {
  playerNote.isPerfect =
    playerNote.distance === 0 &&
    Math.abs(playerNote.length - playerNote.note.length) < perfectLengthTolerance(playerNote.note.length, tolerance);

  playerNote.vibrato = playerNote.distance === 0 && detectVibrato(playerNote.frequencyRecords);
};

export function appendFrequencyToPlayerNotes(
  playerNotes: PlayerNote[],
  record: FrequencyRecord,
  note: Note,
  beatLength: number,
  tolerance: number,
) {
  if (record.frequency === 0) return;
  const noteCandidate = {
    ...record,
    beat: Math.max(0, record.timestamp) / beatLength,
    ...calcDistance(record.frequency, note.pitch, tolerance),
  };
  const lastNote = playerNotes.at(-1);

  const breakToleranceBeat = SINGING_BREAK_TOLERANCE_MS / beatLength;

  const noteEndBeat = note.start + note.length;
  const isThisNoteDifferentThanLast = !lastNote || lastNote.note.start !== note.start;
  const isDistanceDifferent =
    !lastNote || (lastNote.distance !== noteCandidate.distance && !noDistanceNoteTypes.includes(note.type));

  if (
    isThisNoteDifferentThanLast ||
    isDistanceDifferent ||
    noteCandidate.beat - (lastNote.start + lastNote.length) > breakToleranceBeat
  ) {
    const roundedStart = noteCandidate.beat - breakToleranceBeat < note.start ? note.start : noteCandidate.beat;
    playerNotes.push({
      // If this is the first player note for the note, round player note start to note's start
      start: Math.min(isThisNoteDifferentThanLast ? roundedStart : noteCandidate.beat, noteEndBeat),
      length: 0,
      distance: noteCandidate.distance,
      note,
      isPerfect: false,
      vibrato: false,
      frequencyRecords: [
        {
          frequency: noteCandidate.frequency,
          preciseDistance: noteCandidate.preciseDistance,
          timestamp: noteCandidate.timestamp,
        },
      ],
    });

    // Round the last player note length to the end of the note, so it looks a bit smoother
    if (lastNote && note.start !== lastNote.note.start) {
      const lastPlayerNoteEndBeat = lastNote.start + lastNote.length;
      const lastNoteEndBeat = lastNote.note.start + lastNote.note.length;
      const roundedLength =
        lastPlayerNoteEndBeat + breakToleranceBeat > lastNoteEndBeat ? lastNoteEndBeat : lastPlayerNoteEndBeat;
      lastNote.length = Math.max(0, roundedLength - lastNote.start);
      judgeHold(lastNote, tolerance);
    }
  } else {
    lastNote.length = Math.max(0, Math.min(noteCandidate.beat, note.start + note.length) - lastNote.start);
    lastNote.frequencyRecords.push({
      frequency: noteCandidate.frequency,
      timestamp: noteCandidate.timestamp,
      preciseDistance: noteCandidate.preciseDistance,
    });

    judgeHold(lastNote, tolerance);
  }
}
